import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProducts, getJournal } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { PrimaryButton, GhostButton, Overline, SectionHeading } from "@/components/site/Primitives";
import { ProductCard } from "@/components/site/ProductCard";
import { NewsletterSection } from "@/components/site/Newsletter";
import { Almanac } from "@/components/site/Almanac";
import { Reveal } from "@/lib/reveal";

const HUBS = [
  { label: "Pantry & Preserving", to: "/pantry-preserving", img: IMAGES.pantryHub },
  { label: "Herbal Wisdom", to: "/herbal-wisdom", img: IMAGES.herbHub },
  { label: "Recipes", to: "/recipes", img: IMAGES.breadHub },
  { label: "Seasonal Living", to: "/seasonal-living", img: IMAGES.seasonalHub },
];

const TESTIMONIALS = [
  { q: "Opening Agnes's cookbook feels like being handed my grandmother's recipe tin. I have cooked from it every single week.", n: "Eleanor R., Cotswolds" },
  { q: "The Herbal Almanac now lives on my windowsill. It has quietly changed how our whole household rests and heals.", n: "Marianne T., Vermont" },
  { q: "Nothing online has ever made me feel this calm. It is like stepping out of the noise and into a warm kitchen.", n: "Grace W., Yorkshire" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [seasonal, setSeasonal] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    getProducts({ featured: true }).then(setFeatured).catch(() => {});
    getProducts({ seasonal: true }).then(setSeasonal).catch(() => {});
    getJournal().then((a) => setArticles(a.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative">
        <div className="grid lg:grid-cols-12 min-h-[85vh]">
          <div className="lg:col-span-7 relative order-2 lg:order-1">
            <img src={IMAGES.hero} alt="Rosewood Cottage kitchen" className="w-full h-full object-cover min-h-[50vh]" />
            <div className="absolute inset-0 bg-brand-charcoal/10" />
          </div>
          <div className="lg:col-span-5 flex items-center order-1 lg:order-2 bg-brand-cream">
            <div className="px-8 md:px-16 py-20 lg:py-0">
              <Overline>Welcome home</Overline>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] mt-6 mb-6">
                Welcome to<br />Rosewood Cottage
              </h1>
              <p className="font-body text-brand-body text-lg leading-relaxed max-w-md mb-10">
                Timeless recipes, herbal wisdom, and traditional homemaking lovingly preserved for the modern home.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <PrimaryButton to="/library" testid="hero-library-btn">Enter the Cottage Library</PrimaryButton>
                <GhostButton to="/about" testid="hero-about-btn">Meet Agnes</GhostButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Almanac />

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="featured-section">
        <Reveal>
          <SectionHeading overline="From the Cottage Library" title="Treasures worth keeping"
            sub="Each a labour of love — gathered, tested and beautifully set down for your own home." />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-16">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}><ProductCard product={p} /></Reveal>
          ))}
        </div>
        <div className="mt-16"><GhostButton to="/library" testid="view-library-btn">Explore the full library</GhostButton></div>
      </section>

      {/* AGNES STORY */}
      <section className="bg-brand-warm-white" data-testid="agnes-story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal><img src={IMAGES.agnes} alt="Agnes at her table" className="w-full object-cover aspect-[4/5]" /></Reveal>
          <Reveal delay={0.1}>
            <Overline>Meet Agnes</Overline>
            <h2 className="text-4xl sm:text-5xl font-light mt-4 mb-6 leading-tight">A life spent preserving the beautiful and the true</h2>
            <p className="font-body text-brand-body text-lg leading-relaxed mb-5">
              I am Agnes Whitmore — seventy-eight years old, and keeper of Rosewood Cottage for the better part
              of my life. I have spent my years gathering the wisdom that once passed quietly from hand to hand:
              the recipes, the remedies, the small daily arts of a well-kept home.
            </p>
            <p className="font-body text-brand-body text-lg leading-relaxed mb-8 italic">
              "The old ways were never about doing more. They were about doing things with love."
            </p>
            <GhostButton to="/about" testid="story-about-btn">Read my story</GhostButton>
          </Reveal>
        </div>
      </section>

      {/* EXPLORE THE COTTAGE / HUBS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="hubs-section">
        <Reveal><SectionHeading center overline="Wander a while" title="Explore the Cottage" /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {HUBS.map((h, i) => (
            <Reveal key={h.to} delay={i * 0.08}>
              <Link to={h.to} data-testid={`hub-${h.to.slice(1)}`} className="group block relative overflow-hidden">
                <img src={h.img} alt={h.label} className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/70 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-serif text-2xl text-brand-cream leading-tight">{h.label}</h3>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* JOURNAL */}
      <section className="bg-brand-linen/50" data-testid="home-journal-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal><SectionHeading overline="From the Journal" title="Newest from my writing desk" /></Reveal>
          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.08}>
                <Link to={`/journal/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group block bg-brand-warm-white border border-brand-linen hover:border-brand-gold transition-colors duration-500">
                  <img src={a.cover} alt={a.title} className="w-full aspect-[3/2] object-cover" />
                  <div className="p-7">
                    <span className="overline text-[0.62rem]">{a.category} · {a.read_time}</span>
                    <h3 className="font-serif text-2xl mt-3 leading-snug group-hover:text-brand-olive transition-colors">{a.title}</h3>
                    <p className="font-body text-brand-muted mt-3 leading-relaxed">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="testimonials-section">
        <Reveal><SectionHeading center overline="Kind words" title="From cottage kitchens everywhere" /></Reveal>
        <div className="grid md:grid-cols-3 gap-10 mt-16">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <figure className="text-center px-4">
                <blockquote className="font-serif text-2xl italic leading-snug text-brand-charcoal">"{t.q}"</blockquote>
                <figcaption className="overline mt-6 block">{t.n}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SEASONAL COLLECTION */}
      <section className="relative" data-testid="seasonal-section">
        <img src={IMAGES.seasonalBanner} alt="Seasonal wildflowers" className="w-full h-[70vh] object-cover" />
        <div className="absolute inset-0 bg-brand-charcoal/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
            <div className="max-w-md">
              <Overline className="text-brand-cream">This Season</Overline>
              <h2 className="text-4xl sm:text-5xl font-light text-brand-cream mt-4 mb-6 leading-tight">The Seasonal Collection</h2>
              <p className="font-body text-brand-cream/90 text-lg leading-relaxed mb-8">
                A gentle gathering of the products best suited to the turning year — chosen to help you live with the seasons.
              </p>
              <div className="flex flex-wrap gap-4">
                {seasonal.slice(0, 2).map((p) => (
                  <PrimaryButton key={p.slug} to={`/library/${p.slug}`} testid={`seasonal-${p.slug}`}>{p.title}</PrimaryButton>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSection />
    </div>
  );
}
